import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Loader2, Plus, Trash2, Undo2, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import OrgChartCanvas from "./OrgChartCanvas";

function EmployeePicker({ open, onOpenChange, employees, excludeId, onPick, title }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none p-0 max-w-md" data-testid="employee-picker-dialog">
        <DialogHeader className="px-4 pt-4 pb-0">
          <DialogTitle className="text-[14px]">{title}</DialogTitle>
        </DialogHeader>
        <Command className="rounded-none">
          <CommandInput placeholder="Search employees…" data-testid="employee-picker-search" />
          <CommandList className="max-h-72">
            <CommandEmpty>No match.</CommandEmpty>
            {employees.filter((e) => e.id !== excludeId).map((e) => (
              <CommandItem key={e.id} value={`${e.name} ${e.title} ${e.id}`}
                data-testid={`picker-item-${e.id}`}
                onSelect={() => { onPick(e); onOpenChange(false); }}
                className="rounded-none cursor-pointer">
                <span className="text-[13px]">{e.name}</span>
                <span className="text-[11px] text-muted-foreground ml-2 truncate">{e.title}</span>
                <span className="text-[10px] font-mono text-muted-foreground ml-auto">{e.id}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

const DELTA_KEYS = [
  ["headcount", "Headcount", "neutral"],
  ["monthly_cost", "Monthly cost Δ", "cost"],
  ["max_depth", "Max depth", "bad"],
  ["span_violations", "Span violations", "bad"],
  ["layer_violations", "Layer violations", "bad"],
  ["spof_count", "SPOFs", "bad"],
  ["findings_total", "Findings", "bad"],
];

function DeltaStrip({ detail }) {
  return (
    <div className="grid grid-cols-4 lg:grid-cols-7 border border-border bg-white divide-x divide-border" data-testid="sandbox-delta-strip">
      {DELTA_KEYS.map(([key, label, kind]) => {
        const staged = detail.staged.metrics[key];
        const delta = detail.deltas[key];
        if (staged == null && delta == null) return (
          <div key={key} className="px-3 py-2.5"><p className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">{label}</p><p className="text-[14px] font-bold text-slate-400 mt-0.5">—</p></div>
        );
        const good = kind === "bad" && delta < 0;
        const worse = kind === "bad" && delta > 0;
        return (
          <div key={key} className="px-3 py-2.5">
            <p className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">{label}</p>
            <p className="text-[15px] font-bold text-[var(--brand-primary)] mt-0.5">
              {key === "monthly_cost" ? `${delta >= 0 ? "+" : "−"}${Math.abs(delta).toLocaleString()}` : staged}
              {key !== "monthly_cost" && delta !== 0 && delta != null && (
                <span className={`ml-1.5 text-[11px] font-mono ${good ? "text-emerald-600" : worse ? "text-red-600" : "text-slate-500"}`}>
                  {delta > 0 ? `▲${delta}` : `▼${Math.abs(delta)}`}
                </span>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export default function ScenarioSandbox({ canEdit }) {
  const [scenarios, setScenarios] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ title: "", manager: null, est_salary: "" });
  const [mgrPickerOpen, setMgrPickerOpen] = useState(false);

  const loadList = useCallback(() => api.get("/org/scenarios").then((r) => setScenarios(r.data.scenarios)).catch(() => setScenarios([])), []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback((id) => {
    setDetail(null);
    api.get(`/org/scenarios/${id}/diagnostics`).then((r) => setDetail(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  useEffect(() => { if (activeId) { setSelectedNode(null); loadDetail(activeId); } }, [activeId, loadDetail]);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/org/scenarios", { name });
      setName("");
      await loadList();
      setActiveId(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const patch = async (fields) => {
    try {
      await api.patch(`/org/scenarios/${activeId}`, fields);
      loadDetail(activeId);
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/org/scenarios/${id}`);
      if (id === activeId) { setActiveId(null); setDetail(null); }
      loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sc = detail?.scenario;
  const reassign = (target) => {
    patch({ reassignments: { ...sc.reassignments, [selectedNode.id]: target.id } });
    setSelectedNode(null);
  };
  const removeNode = () => {
    if (selectedNode.is_proposed) {
      patch({ proposed_roles: sc.proposed_roles.filter((r) => r.id !== selectedNode.id) });
    } else {
      patch({ removed: [...sc.removed, selectedNode.id] });
    }
    setSelectedNode(null);
  };
  const addRole = () => {
    if (!roleForm.title.trim() || !roleForm.manager) return;
    patch({
      proposed_roles: [...sc.proposed_roles, {
        id: `NEW-${Date.now() % 1000000}`, title: roleForm.title.trim(),
        manager_id: roleForm.manager.id, est_salary: Number(roleForm.est_salary) || 0,
      }],
    });
    setRoleForm({ title: "", manager: null, est_salary: "" });
    setRoleOpen(false);
  };

  const stagedEmployees = detail?.staged_chart || [];
  const byId = Object.fromEntries(stagedEmployees.map((e) => [e.id, e]));
  const changeRows = sc ? [
    ...Object.entries(sc.reassignments).map(([eid, mid]) => ({
      key: `re-${eid}`, label: `${byId[eid]?.name || eid} → reports to ${byId[mid]?.name || mid}`,
      undo: () => { const r = { ...sc.reassignments }; delete r[eid]; patch({ reassignments: r }); },
    })),
    ...sc.removed.map((eid) => ({
      key: `rm-${eid}`, label: `Removed ${eid} (reports rolled up)`,
      undo: () => patch({ removed: sc.removed.filter((x) => x !== eid) }),
    })),
    ...sc.proposed_roles.map((r) => ({
      key: `pr-${r.id}`, label: `Proposed: ${r.title} under ${byId[r.manager_id]?.name || r.manager_id} (QAR ${Number(r.est_salary).toLocaleString()})`,
      undo: () => patch({ proposed_roles: sc.proposed_roles.filter((x) => x.id !== r.id) }),
    })),
  ] : [];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-5" data-testid="scenario-sandbox">
      {/* Scenario rail */}
      <div className="bg-white border border-border self-start">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold flex items-center gap-1.5">
            <FlaskConical className="h-3.5 w-3.5" /> Scenarios
          </p>
          {canEdit && (
            <div className="flex gap-1.5 mt-2.5">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New scenario name"
                data-testid="sandbox-create-input" className="rounded-none h-8 text-[12px]"
                onKeyDown={(e) => e.key === "Enter" && create()} />
              <Button onClick={create} disabled={creating || !name.trim()} data-testid="sandbox-create-btn"
                className="rounded-none h-8 w-8 p-0 bg-[var(--brand-primary)] hover:bg-slate-700 shrink-0">
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              </Button>
            </div>
          )}
        </div>
        <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
          {scenarios === null ? <Skeleton className="h-20 m-3 rounded-none" /> : scenarios.map((s) => (
            <div key={s.id}
              className={`px-4 py-2.5 flex items-center gap-2 cursor-pointer ${activeId === s.id ? "bg-teal-50 border-l-2 border-l-teal-600" : "hover:bg-slate-50 border-l-2 border-l-transparent"}`}
              data-testid={`scenario-item-${s.id}`}
              onClick={() => setActiveId(s.id)}>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-medium text-slate-800 truncate">{s.name}</p>
                <p className="text-[10px] font-mono text-muted-foreground">{s.creator_name} · {s.updated_at?.slice(0, 10)}</p>
              </div>
              {canEdit && (
                <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} data-testid={`scenario-delete-${s.id}`}
                  className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          ))}
          {scenarios?.length === 0 && (
            <p className="px-4 py-5 text-[12px] text-muted-foreground">
              {canEdit ? "No scenarios yet — create one to stage a restructuring safely." : "No scenarios shared yet."}
            </p>
          )}
        </div>
      </div>

      {/* Workbench */}
      <div className="space-y-4 min-w-0">
        {!activeId && (
          <div className="border border-border bg-white p-8 text-[13px] text-muted-foreground">
            Select or create a scenario. All changes are staged — the real organization is never modified.
          </div>
        )}
        {activeId && !detail && <Skeleton className="h-96 w-full rounded-none" />}
        {activeId && detail && (
          <>
            <DeltaStrip detail={detail} />

            <div className="flex items-center gap-2 flex-wrap bg-white border border-border px-4 py-2.5">
              {canEdit ? (
                selectedNode ? (
                  <>
                    <span className="text-[12px] font-semibold text-slate-800">{selectedNode.name}</span>
                    <span className="text-[11px] text-muted-foreground">{selectedNode.title}</span>
                    <Button onClick={() => setPickerOpen(true)} data-testid="sandbox-reassign-btn"
                      className="rounded-none h-7 text-[11px] bg-teal-700 hover:bg-teal-800 ml-2 px-2.5">Reassign to…</Button>
                    <Button onClick={removeNode} variant="outline" data-testid="sandbox-remove-btn"
                      className="rounded-none h-7 text-[11px] text-red-600 border-red-300 hover:bg-red-50 px-2.5">
                      {selectedNode.is_proposed ? "Drop proposed role" : "Remove from org"}
                    </Button>
                    <button onClick={() => setSelectedNode(null)} className="text-[11px] text-muted-foreground hover:text-slate-700 ml-1">clear</button>
                  </>
                ) : (
                  <span className="text-[12px] text-muted-foreground">Click a node to reassign or remove it.</span>
                )
              ) : (
                <span className="text-[12px] text-muted-foreground">Read-only view — scenario editing is limited to admins and department heads.</span>
              )}
              {canEdit && (
                <Button onClick={() => setRoleOpen(true)} variant="outline" data-testid="sandbox-add-role-btn"
                  className="rounded-none h-7 text-[11px] ml-auto px-2.5">
                  <UserPlus className="h-3.5 w-3.5" /> Add proposed role
                </Button>
              )}
            </div>

            <OrgChartCanvas
              employees={stagedEmployees}
              overlay="reporting"
              selectedId={selectedNode?.id}
              onSelect={(e) => canEdit && setSelectedNode(e)}
              height={480}
              testId="sandbox-chart-canvas"
            />

            {changeRows.length > 0 && (
              <div className="bg-white border border-border" data-testid="sandbox-changes">
                <p className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold border-b border-border">
                  Staged changes ({changeRows.length})
                </p>
                <div className="divide-y divide-border">
                  {changeRows.map((c) => (
                    <div key={c.key} className="px-4 py-2 flex items-center justify-between">
                      <span className="text-[12px] text-slate-700">{c.label}</span>
                      {canEdit && (
                        <button onClick={c.undo} className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                          data-testid={`sandbox-undo-${c.key}`}>
                          <Undo2 className="h-3 w-3" /> undo
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <EmployeePicker open={pickerOpen} onOpenChange={setPickerOpen} employees={stagedEmployees}
        excludeId={selectedNode?.id} onPick={reassign} title={`New manager for ${selectedNode?.name || ""}`} />
      <EmployeePicker open={mgrPickerOpen} onOpenChange={setMgrPickerOpen} employees={stagedEmployees}
        onPick={(e) => setRoleForm((f) => ({ ...f, manager: e }))} title="Reports to…" />

      <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
        <DialogContent className="rounded-none max-w-sm" data-testid="add-role-dialog">
          <DialogHeader><DialogTitle className="text-[15px]">Add proposed role</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Role title</Label>
              <Input value={roleForm.title} onChange={(e) => setRoleForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Engineering Team Lead" data-testid="add-role-title" className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Reports to</Label>
              <Button variant="outline" onClick={() => setMgrPickerOpen(true)} data-testid="add-role-manager-btn"
                className="rounded-none w-full justify-start mt-1 text-[13px] font-normal">
                {roleForm.manager ? `${roleForm.manager.name} — ${roleForm.manager.title}` : "Select manager…"}
              </Button>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Est. monthly salary (QAR)</Label>
              <Input type="number" value={roleForm.est_salary} onChange={(e) => setRoleForm((f) => ({ ...f, est_salary: e.target.value }))}
                placeholder="30000" data-testid="add-role-salary" className="rounded-none mt-1" />
            </div>
            <Button onClick={addRole} disabled={!roleForm.title.trim() || !roleForm.manager} data-testid="add-role-submit"
              className="rounded-none w-full bg-teal-700 hover:bg-teal-800">Stage role</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
